// Utility for managing push notifications subscription
import api from '../api';

/**
 * Register service worker and request notification permission
 */
export const initPushNotifications = async () => {
  try {
    // Check if browser supports service workers
    if (!('serviceWorker' in navigator)) {
      console.warn('[Push] Service Workers not supported');
      return false;
    }

    // Register service worker
    const registration = await navigator.serviceWorker.register('/service-worker.js', {
      scope: '/',
    });
    console.log('[Push] Service Worker registered:', registration);

    // Check if browser supports push notifications
    if (!('Notification' in window)) {
      console.warn('[Push] Notifications not supported');
      return false;
    }

    // DON'T auto-subscribe even if permission is granted
    // Let user explicitly subscribe via button or prompt
    console.log('[Push] Initialization complete. Permission:', Notification.permission);
    return true;
  } catch (error) {
    console.error('[Push] Initialization error:', error);
    return false;
  }
};

/**
 * Request notification permission and subscribe
 */
export const requestNotificationPermission = async () => {
  try {
    if (!('Notification' in window)) {
      alert('Your browser does not support notifications');
      return false;
    }

    // Request permission
    const permission = await Notification.requestPermission();
    console.log('[Push] Permission result:', permission);

    if (permission !== 'granted') {
      console.log('[Push] Notification permission denied');
      return false;
    }

    // Get service worker registration
    const registration = await navigator.serviceWorker.ready;
    await subscribeToPushNotifications(registration);
    return true;
  } catch (error) {
    console.error('[Push] Permission request error:', error);
    return false;
  }
};

/**
 * Subscribe device to push notifications
 */
export const subscribeToPushNotifications = async (registration) => {
  try {
    // Get VAPID public key from backend
    const vapidRes = await api.get('/api/notifications/vapid-key');
    const publicKey = vapidRes.data?.publicKey;

    if (!publicKey) {
      throw new Error('Failed to get VAPID public key');
    }

    // Convert public key to Uint8Array
    const convertedVapidKey = urlBase64ToUint8Array(publicKey);

    // Create subscription
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: convertedVapidKey,
    });

    console.log('[Push] Subscription created:', subscription);

    // Send subscription to backend
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: 'Bearer ' + token } : {};

    const response = await api.post(
      '/api/notifications/subscribe',
      { subscription: subscription.toJSON() },
      { headers }
    );

    console.log('[Push] Subscription registered with server:', response.data);
    
    // Mark that user has explicitly subscribed
    localStorage.setItem('pushNotificationSubscribed', 'true');
    localStorage.removeItem('pushNotificationUnsubscribed');
    
    return true;
  } catch (error) {
    console.error('[Push] Subscription error:', error);
    return false;
  }
};

/**
 * Unsubscribe from push notifications
 */
export const unsubscribeFromPushNotifications = async () => {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      console.log('[Push] No active subscription');
      return true;
    }

    // Unsubscribe from browser
    await subscription.unsubscribe();
    console.log('[Push] Unsubscribed from browser push manager');

    // Notify backend
    await api.post('/api/notifications/unsubscribe', {
      endpoint: subscription.endpoint,
    });

    console.log('[Push] Unsubscribed from server');
    
    // Mark that user has explicitly unsubscribed
    localStorage.setItem('pushNotificationUnsubscribed', 'true');
    localStorage.removeItem('pushNotificationSubscribed');
    
    return true;
  } catch (error) {
    console.error('[Push] Unsubscribe error:', error);
    return false;
  }
};

/**
 * Check if user is already subscribed
 */
export const checkSubscriptionStatus = async () => {
  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.log('[Push] Service Worker or PushManager not supported');
      return false;
    }

    // Check localStorage first for faster response
    const explicitlyUnsubscribed = localStorage.getItem('pushNotificationUnsubscribed');
    if (explicitlyUnsubscribed === 'true') {
      console.log('[Push] User explicitly unsubscribed');
      return false;
    }

    // Wait for service worker to be ready
    const registration = await navigator.serviceWorker.ready;
    
    if (!registration) {
      console.log('[Push] Service Worker registration not available');
      return false;
    }

    // Get the subscription from push manager
    const subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      console.log('[Push] Active subscription found:', subscription.endpoint.substring(0, 50) + '...');
      // Update localStorage to reflect actual subscription state
      localStorage.setItem('pushNotificationSubscribed', 'true');
      localStorage.removeItem('pushNotificationUnsubscribed');
      return true;
    } else {
      console.log('[Push] No active subscription');
      // Only mark as unsubscribed if it was previously subscribed
      const wasSubscribed = localStorage.getItem('pushNotificationSubscribed');
      if (wasSubscribed === 'true') {
        localStorage.setItem('pushNotificationUnsubscribed', 'true');
        localStorage.removeItem('pushNotificationSubscribed');
      }
      return false;
    }
  } catch (error) {
    console.error('[Push] Status check error:', error);
    return false;
  }
};

/**
 * Convert VAPID public key from base64 to Uint8Array
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
